import Button from "@mui/material/Button";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import {
  AGENCY_DIS_INDEX,
  AGENCY_SB_INDEX,
  AGENCY_SNC_INDEX,
  AGENCY_TOO_INDEX,
} from "./Sidebar";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useCookies } from "react-cookie";
import GetInformation from "./GetInformation";
import {
  buildApiDebugSummary,
  buildCreateUploadUrlRequest,
  buildResetUploadPathRequest,
  buildUploadFormRequest,
  callCreateUploadUrl,
  callResetUploadPath,
  callUploadForm,
  extractApiErrorMessage,
  isApiSuccess,
  uploadFileWithPresignedUrl,
} from "./fileUploadApi";
import { agencyInformation } from "./Sidebar";
import { formatDate } from "./momTable";

const UPLOAD_PHASES = {
  IDLE: "idle",
  VALIDATING: "validating",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  COMPLETED: "completed",
  ERROR: "error",
};

const UPLOAD_PHASE_LABELS = {
  [UPLOAD_PHASES.IDLE]: "待機中",
  [UPLOAD_PHASES.VALIDATING]: "アップロード対象確認中",
  [UPLOAD_PHASES.UPLOADING]: "S3 アップロード中",
  [UPLOAD_PHASES.PROCESSING]: "後続処理 API 実行中",
  [UPLOAD_PHASES.COMPLETED]: "完了",
  [UPLOAD_PHASES.ERROR]: "エラー",
};

function FileUploader(props) {
  // propsを受け取る
  const { sale_agent } = props;

  // ファイルアップロード関連のステート
  const [filesToUpload, setFilesToUpload] = useState([]); // 選択されたファイル
  const [uploadfileKind, setUploadFileKind] = useState({}); // ファイルの種類（purchaseOrder, orderForm）
  const [uploadedFiles, setUploadedFiles] = useState([]); // アップロードされたファイル
  const [uploadStatus, setUploadStatus] = useState({}); // ファイルアップロードステータス（「Uploading」, 「Uploaded」, 「Upload failed」）
  const [uploadPhase, setUploadPhase] = useState(UPLOAD_PHASES.IDLE); // 画面全体の進行フェーズ
  const [lastActiveUploadPhase, setLastActiveUploadPhase] = useState(
    UPLOAD_PHASES.IDLE,
  ); // エラー直前の進行フェーズ
  const [uploadSessionPath, setUploadSessionPath] = useState(""); // 申請単位の path
  const [uploadTargetsByFileName, setUploadTargetsByFileName] = useState({}); // createUploadUrl が返したファイル単位のアップロード情報
  const [dataDataSts, setDataDataSts] = useState({}); //受注日、出荷予定日
  const [apiCompleteStatus, setApiCompleteStatus] = useState(""); //API呼び出だし（""：未呼び出し、"ok" 呼び出し正常完了、"（エラーメッセージ）"：呼び出しエラー内容）
  const [userEmail, setUserEmail] = useState(""); // ログインユーザーEmail
  const [, setUserName] = useState(""); // ログインユーザー名
  const [, setCookie, removeCookie] = useCookies(); // Cookieのユーザー権限
  const [, setVersionId] = useState(null); // バージョンIDの管理

  const [isWaitCursor, setIsWaitCursor] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const updateUploadPhase = (nextPhase) => {
    if (nextPhase !== UPLOAD_PHASES.ERROR) {
      setLastActiveUploadPhase(nextPhase);
    }
    setUploadPhase(nextPhase);
  };

  useEffect(() => {
    if (errorMessage) {
      setIsWaitCursor(false);
      setUploadPhase(UPLOAD_PHASES.ERROR);
    }
  }, [errorMessage]);

  const hasUploadFailure =
    Object.values(uploadStatus).includes("Upload failed");
  const isRetryMode =
    uploadPhase === UPLOAD_PHASES.ERROR ||
    hasUploadFailure ||
    errorMessage !== "" ||
    (apiCompleteStatus !== "" && apiCompleteStatus !== "ok");
  const isRegisterButtonDisabled =
    isWaitCursor ||
    (!isRetryMode &&
      filesToUpload.every(
        (file) =>
          uploadStatus[file.name] === "Uploaded" ||
          uploadStatus[file.name] === "Uploading",
      ));

  const getUploadErrorPhaseMessage = () => {
    return UPLOAD_PHASE_LABELS[lastActiveUploadPhase] || "不明";
  };

  const handleAllClear = () => {
    setFilesToUpload([]);
    setUploadedFiles([]);
    setUploadStatus({});
    setUploadFileKind({});
    updateUploadPhase(UPLOAD_PHASES.IDLE);
    setUploadSessionPath("");
    setUploadTargetsByFileName({});
    setApiCompleteStatus("");
    setErrorMessage("");
    setIsWaitCursor(false);
  };

  /*****************************************************
  // ファイル削除後の処理
  *****************************************************/

  const handleDeleteFile = (index) => {
    const updatedFiles = [...filesToUpload];
    const deletedFile = updatedFiles[index];

    updatedFiles.splice(index, 1);
    setFilesToUpload(updatedFiles);

    const updatedUploadFileKind = { ...uploadfileKind };
    delete updatedUploadFileKind[deletedFile.name];
    setUploadFileKind(updatedUploadFileKind);

    const updatedUploadStatus = { ...uploadStatus };
    delete updatedUploadStatus[deletedFile.name];
    setUploadStatus(updatedUploadStatus);

    const updatedUploadTargets = { ...uploadTargetsByFileName };
    delete updatedUploadTargets[deletedFile.name];
    setUploadTargetsByFileName(updatedUploadTargets);

    if (updatedFiles.length === 0 && uploadedFiles.length === 0) {
      updateUploadPhase(UPLOAD_PHASES.IDLE);
      setUploadSessionPath("");
      setUploadTargetsByFileName({});
    }
  };

  /*****************************************************
  // 受注日、出荷日の初期化
  *****************************************************/

  const today = new Date();
  const todayDateString =
    today.getFullYear() + "/" + (today.getMonth() + 1) + "/" + today.getDate();

  let shipDate = new Date();

  for (let i = 0; i < 2; i++) {
    shipDate.setDate(shipDate.getDate() + 1);
    while (shipDate.getDay() === 6 || shipDate.getDay() === 0) {
      shipDate.setDate(shipDate.getDate() + 1);
    }
  }

  const shipDateString =
    shipDate.getFullYear() +
    "/" +
    (shipDate.getMonth() + 1) +
    "/" +
    shipDate.getDate();

  /*****************************************************
  // API 呼び出し　（pre
  *****************************************************/
  const preCheckUploadFileExec = async (uploadPath) => {
    updateUploadPhase(UPLOAD_PHASES.PROCESSING);
    await uploadFileExec(uploadPath, sale_agent.ocrModelNo);
    setIsWaitCursor(false);
  };

  /*****************************************************
  // API 呼び出し（core
  *****************************************************/
  const uploadFileExec = async (uploadPath, ocrModelNo) => {
    try {
      const requestData = buildUploadFormRequest({
        userEmail,
        orderDate: dataDataSts.orderDate,
        shipDate: dataDataSts.shipDate,
        distributor: ocrModelNo,
        inputPattern: sale_agent.inFilePattern,
        uploadPath,
      });

      console.log("API call (uploadForm request", requestData);
      const response = await callUploadForm(requestData);
      console.log("API call (uploadForm response", response);

      // HTTP ステータスコードに応じたエラーハンドリング
      if (!isApiSuccess(response)) {
        console.error(
          "uploadForm API returned non-success response",
          buildApiDebugSummary(response),
        );
        throw new Error(extractApiErrorMessage(response, "Error"));
      }
      setApiCompleteStatus("ok");
      updateUploadPhase(UPLOAD_PHASES.COMPLETED);
    } catch (error) {
      setUploadPhase(UPLOAD_PHASES.ERROR);
      if (error.response) {
        const { status, statusText } = error.response;
        setApiCompleteStatus(`Error! HTTP Status: ${status} ${statusText}`);
      } else {
        setApiCompleteStatus(error.message);
      }
    }
  };

  /*****************************************************
  // Too 申請書のペア判定
  *****************************************************/
  const checkUploadFileName = () => {
    let alertMsg;
    if (sale_agent.apiID === AGENCY_TOO_INDEX) {
      const invalidFiles = filesToUpload
        .filter((file) => uploadfileKind[file.name] === "error")
        .map((file) => file.path);

      if (invalidFiles.length !== 0) {
        alertMsg = "無効なファイル名のためアップロードできません\n";
        invalidFiles.forEach((fileName) => {
          alertMsg += "\n　" + fileName;
        });
        alertMsg +=
          "\n\nファイル命名規則を確認のうえ、選択したファイルを見直してください\n";
        alert(alertMsg);
        return false;
      }

      const validFiles = filesToUpload.filter(
        (file) => uploadfileKind[file.name] !== "error",
      );
      const baseNames = validFiles.map((file) => file.path.slice(0, -6));

      const unpaired = [];

      baseNames.forEach((baseName, index, self) => {
        if (
          self.indexOf(baseName) === index &&
          self.lastIndexOf(baseName) === index
        ) {
          unpaired.push(validFiles[index].path);
        }
      });

      if (unpaired.length !== 0) {
        alertMsg =
          "無効なファイル (ファイル名) または、ペアになる申請書が見つかりません\n";
        unpaired.forEach((fileName) => {
          alertMsg += "\n　" + fileName;
        });
        alertMsg +=
          "\n\nファイル命名規則を確認のうえ、選択したファイルを見直してください\n";
        alert(alertMsg);
        return false;
      }
    } else if (
      sale_agent.apiID === AGENCY_DIS_INDEX ||
      sale_agent.apiID === AGENCY_SB_INDEX
    ) {
      const values = Object.values(uploadfileKind);

      if (values.includes("purchaseOrder")) {
        if (values.includes("orderForm")) {
          alertMsg = "";
        } else {
          alertMsg = "\n\n注文書類を指定してください\n";
        }
      } else {
        if (values.includes("orderForm")) {
          alertMsg = "\n\n購入申請書を指定してください\n";
        } else {
          alertMsg = "\n\n購入申請書および注文書類を指定してください\n";
        }
      }
      if (alertMsg !== "") {
        alert(alertMsg);
        return false;
      } else return true;
    }
    return true;
  };

  const getApiFileKind = (fileName) => {
    if (sale_agent.apiID === AGENCY_SNC_INDEX) {
      return "bo";
    }

    return uploadfileKind[fileName]?.startsWith("purchaseOrder") ? "po" : "of";
  };

  const getTooType = (fileName) => {
    return uploadfileKind[fileName]?.endsWith("_th") ? "h" : "c";
  };

  const buildCreateUploadGroups = () => {
    const groupsByKey = {};

    filesToUpload.forEach((file) => {
      const fileCategory = uploadfileKind[file.name];

      if (!fileCategory || fileCategory === "error") {
        return;
      }

      const fileKind = getApiFileKind(file.name);
      const tooType =
        sale_agent.apiID === AGENCY_TOO_INDEX
          ? getTooType(file.name)
          : undefined;
      const groupKey = tooType ? `${tooType}:${fileKind}` : fileKind;

      if (!groupsByKey[groupKey]) {
        groupsByKey[groupKey] = {
          distributor: sale_agent.ocrModelNo,
          tooType,
          fileKind,
          files: [],
        };
      }

      groupsByKey[groupKey].files.push(file);
    });

    const fileKindPriority = {
      po: 0,
      of: 1,
      bo: 2,
    };

    return Object.values(groupsByKey).sort((leftGroup, rightGroup) => {
      const leftPriority = fileKindPriority[leftGroup.fileKind] ?? 99;
      const rightPriority = fileKindPriority[rightGroup.fileKind] ?? 99;

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return (leftGroup.tooType || "").localeCompare(rightGroup.tooType || "");
    });
  };

  const prepareUploadSession = async () => {
    const uploadGroups = buildCreateUploadGroups();
    const nextUploadTargetsByFileName = {};
    let currentPath = "";

    try {
      for (const uploadGroup of uploadGroups) {
        const requestData = buildCreateUploadUrlRequest({
          distributor: uploadGroup.distributor,
          tooType: uploadGroup.tooType,
          fileKind: uploadGroup.fileKind,
          fileNames: uploadGroup.files.map((file) => file.name),
          path: currentPath,
        });

        console.log("API call (createUploadUrl request", requestData);
        const response = await callCreateUploadUrl(requestData);
        console.log(
          "API call (createUploadUrl response",
          buildApiDebugSummary(response),
        );
        if (!isApiSuccess(response)) {
          console.error(
            "createUploadUrl API returned non-success response",
            buildApiDebugSummary(response),
          );
          throw new Error(
            extractApiErrorMessage(response, "createUploadUrl Error"),
          );
        }

        currentPath = response.data.path;
        response.data.files.forEach((uploadTarget) => {
          nextUploadTargetsByFileName[uploadTarget.fileName] = uploadTarget;
        });
      }
    } catch (error) {
      await resetUploadSession(currentPath);
      throw error;
    }

    setUploadSessionPath(currentPath);
    setUploadTargetsByFileName(nextUploadTargetsByFileName);

    return {
      path: currentPath,
      uploadTargetsByFileName: nextUploadTargetsByFileName,
    };
  };

  const resetUploadSession = async (path) => {
    if (!path) {
      return;
    }

    try {
      const requestData = buildResetUploadPathRequest({
        distributor: sale_agent.ocrModelNo,
        path,
      });

      console.log("API call (resetUploadPath request", requestData);
      const response = await callResetUploadPath(requestData);
      console.log("API call (resetUploadPath response", response);

      if (!isApiSuccess(response)) {
        console.error(
          "resetUploadPath API returned non-success response",
          buildApiDebugSummary(response),
        );
      }
    } catch (error) {
      console.error("resetUploadSession failed", error);
    }
  };

  /*****************************************************
  // S3 へのファイルアップロード
  *****************************************************/
  const handleUpload = async () => {
    updateUploadPhase(UPLOAD_PHASES.VALIDATING);
    if (checkUploadFileName() === false) {
      updateUploadPhase(UPLOAD_PHASES.IDLE);
      return;
    }

    setIsWaitCursor(true);

    setApiCompleteStatus("");
    setErrorMessage("");
    setUploadedFiles([]);
    setUploadStatus({});
    setUploadTargetsByFileName({});
    setUploadSessionPath("");

    let preparedSessionPath = "";
    let preparedUploadTargetsByFileName = {};

    try {
      const preparedSession = await prepareUploadSession();
      preparedSessionPath = preparedSession.path;
      preparedUploadTargetsByFileName = preparedSession.uploadTargetsByFileName;
      updateUploadPhase(UPLOAD_PHASES.UPLOADING);
    } catch (error) {
      setUploadPhase(UPLOAD_PHASES.ERROR);
      setIsWaitCursor(false);
      setErrorMessage(error.message);
      return;
    }

    const uploadPromises = filesToUpload.map(
      (file) =>
        new Promise((resolve, reject) => {
          const uploadTarget = preparedUploadTargetsByFileName[file.name];

          if (!uploadTarget?.uploadUrl) {
            reject(
              new Error(
                `${file.name} のアップロード URL を取得できませんでした`,
              ),
            );
            return;
          }

          setUploadStatus((prevStatus) => ({
            ...prevStatus,
            [file.name]: "Uploading",
          }));

          uploadFileWithPresignedUrl({
            uploadUrl: uploadTarget.uploadUrl,
            file,
            contentType: "application/pdf",
          })
            .then(() => {
              const uploadedFileUrl =
                uploadTarget.viewUrl ||
                (uploadTarget.uploadUrl
                  ? uploadTarget.uploadUrl.split("?")[0]
                  : "");

              setUploadedFiles((prevuploadedFiles) => [
                ...prevuploadedFiles,
                {
                  fileName: file.name,
                  url: uploadedFileUrl,
                },
              ]);
              setUploadStatus((prevStatus) => ({
                ...prevStatus,
                [file.name]: "Uploaded",
              }));
              resolve();
            })
            .catch((uploadError) => {
              console.log("◆◆◆ Error ◆◆◆", uploadError);
              setUploadStatus((prevStatus) => ({
                ...prevStatus,
                [file.name]: "Upload failed",
              }));
              setErrorMessage(
                "ファイルのアップロードに失敗しました: " +
                  (uploadError?.message || "Unknown error"),
              );
              reject(uploadError);
            });
        }),
    );

    const uploadResults = await Promise.allSettled(uploadPromises);
    const firstRejectedUpload = uploadResults.find(
      (result) => result.status === "rejected",
    );

    if (firstRejectedUpload) {
      const error = firstRejectedUpload.reason;
      await resetUploadSession(preparedSessionPath);
      setUploadStatus({});
      setUploadedFiles([]);
      setUploadTargetsByFileName({});
      setUploadSessionPath(preparedSessionPath);
      setUploadPhase(UPLOAD_PHASES.ERROR);
      setIsWaitCursor(false);
      setErrorMessage((prevErrorMessage) => prevErrorMessage || error.message);
      console.log("◆◆◆ Error ◆◆◆ Some uploads failed", error);
      return;
    }

    await preCheckUploadFileExec(preparedSessionPath);
  };

  /*****************************************************
  // 受注日、出荷予定日の設定
  *****************************************************/
  const handleSetDataDataSts = (orderDate, newDate) => {
    let obj = {
      ...dataDataSts,
      [orderDate]: newDate,
    };
    setDataDataSts(obj);
  };

  /*****************************************************
  // 受注日、出荷予定日の表タイトル
  *****************************************************/
  const renderOnlyItemTableHeader = () => {
    return (
      <TableHead>
        <TableRow sx={{ backgroundColor: "#dddddd" }}>
          <TableCell
            sx={{ width: "20%", textAlign: "center", paddingBottom: "0" }}
          >
            販社
          </TableCell>
          <TableCell sx={{ textAlign: "center", paddingBottom: "0" }}>
            受注日
          </TableCell>
          <TableCell sx={{ textAlign: "center", paddingBottom: "0" }}>
            出荷予定日
          </TableCell>
          <TableCell
            sx={{ width: "20%", textAlign: "center", paddingBottom: "0" }}
          >
            登録者
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  /*****************************************************
  // 受注日、出荷予定日の表データ
  *****************************************************/
  const renderOnlyItemTableData = (orderDate, shipDate) => {
    return (
      <TableRow sx={{ backgroundColor: "#dddddd" }}>
        {/*****************************************************
        //  流通販社
        *****************************************************/}
        <TableCell
          sx={{
            textAlign: "center",
          }}
        >
          {agencyInformation[sale_agent.apiID].displayName}
        </TableCell>
        {/*****************************************************
        //  受注日
        *****************************************************/}
        <TableCell
          sx={{
            textAlign: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                dataDataSts.orderDate !== undefined
                  ? dataDataSts.orderDate
                  : todayDateString &&
                    handleSetDataDataSts("orderDate", todayDateString)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              onChange={(date) =>
                handleSetDataDataSts("orderDate", date.format("YYYY/MM/DD"))
              }
              renderInput={(params) => (
                <TextField
                  sx={{
                    backgroundColor: "#f5f5f5",
                  }}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    value: formatDate(params.inputProps.value),
                    readOnly: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </TableCell>

        {/*****************************************************
        //  出荷予定日
        *****************************************************/}
        <TableCell
          sx={{
            textAlign: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                dataDataSts.shipDate !== undefined
                  ? dataDataSts.shipDate
                  : shipDateString &&
                    handleSetDataDataSts("shipDate", shipDateString)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              onChange={(date) =>
                handleSetDataDataSts("shipDate", date.format("YYYY/MM/DD"))
              }
              renderInput={(params) => (
                <TextField
                  sx={{
                    backgroundColor: "#f5f5f5",
                  }}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    value: formatDate(params.inputProps.value),
                    readOnly: true,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </TableCell>
        {/*****************************************************
        //  登録者
        *****************************************************/}
        <TableCell sx={{ textAlign: "center" }}>
          {userEmail.split("@")[0]}
        </TableCell>
      </TableRow>
    );
  };

  /*****************************************************
  // 全角を半角に変換する関数
  *****************************************************/
  function toHalfWidth(str) {
    if (typeof str !== "string") return "";
    return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0),
    );
  }

  /*****************************************************
  // ファイル種別判定
  *****************************************************/
  function checkOrderForm(apiID, fileName) {
    if (apiID === AGENCY_TOO_INDEX) {
      const lastTwoChars = fileName.slice(-6);
      const topTwoChars = toHalfWidth(fileName.slice(0, 2));

      switch (lastTwoChars) {
        case "-1.pdf":
          switch (topTwoChars) {
            case "TM":
              return "orderForm"; // 注文書
            case "S-":
              return "orderForm_th"; // 発注書
            default:
              return "error";
          }
        case "-2.pdf":
          switch (topTwoChars) {
            case "TM":
              return "purchaseOrder"; // 購入申請書（注文書
            case "S-":
              return "purchaseOrder_th"; // 購入申請書（発注書
            default:
              return "error";
          }
        default:
          return "error";
      }
    } else if (apiID === AGENCY_SB_INDEX) {
      const topTwoChars = fileName.slice(0, 2);

      switch (topTwoChars) {
        case "HA":
          return "orderForm"; // 発注書
        default:
          return "purchaseOrder"; // 購入申請書
      }
    } else if (apiID === AGENCY_DIS_INDEX) {
      const topTwoChars = fileName.slice(0, 2);

      switch (topTwoChars) {
        case "20":
          return "orderForm"; // 注文書
        default:
          return "purchaseOrder"; // 購入申請書
      }
    } else {
      // AGENCY_SNC_INDEX
      return "purchaseOrder";
    }
  }

  /*****************************************************
  // 注意書きなど（ファイル命名規則
  *****************************************************/
  const tooInformationTitle = "購入申請書と注文書類の判別方法";
  const tooInformationText = [
    {
      paddingLeft: 4,
      text: "・ファイル名の先頭が「S-」は発注書とその購入申請書、「TM」は注文書とその購入申請書と判別",
    },
    {
      paddingLeft: 4,
      text: "・ファイル名の最後が「-1.pdf」は発注書または注文書、「-2.pdf」は購入申請書と判別",
    },
  ];

  const synInformationTitle = "購入申請書と注文書類の判別方法";
  const synInformationText = [
    {
      paddingLeft: 4,
      text: "PDFを OCR しタイトルで（「Morisawa Fonts」は購入申請書、「発注書」は発注書と）判別",
    },
  ];

  const sbInformationTitle = "購入申請書と注文書類の判別方法";
  const sbInformationText = [
    {
      paddingLeft: 4,
      text: "・ファイル名の先頭が「HA」は発注書、それ以外は購入申請書と判別",
    },
  ];

  const disInformationTitle = "購入申請書と注文書類の判別方法";
  const disInformationText = [
    {
      paddingLeft: 4,
      text: "・ファイル名の先頭が「20」は注文書、それ以外は購入申請書と判別",
    },
  ];

  const [open, setOpen] = React.useState(false);
  const handleClick = () => {
    setOpen(!open);
  };

  const RenderList = ({ handleClick, open, apiID }) => {
    return (
      <List
        sx={{ width: "100%" }}
        component="nav"
        aria-labelledby="nested-list-subheader"
      >
        <ListItemButton onClick={handleClick}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2">
                {apiID === AGENCY_TOO_INDEX
                  ? tooInformationTitle
                  : apiID === AGENCY_SNC_INDEX
                    ? synInformationTitle
                    : apiID === AGENCY_SB_INDEX
                      ? sbInformationTitle
                      : disInformationTitle}
              </Typography>
            }
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        {
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {(apiID === AGENCY_TOO_INDEX
                ? tooInformationText
                : apiID === AGENCY_SNC_INDEX
                  ? synInformationText
                  : apiID === AGENCY_SB_INDEX
                    ? sbInformationText
                    : disInformationText
              ).map((item, index) => (
                <ListItemButton sx={{ pl: item.paddingLeft }} key={index}>
                  <Typography variant="body2" sx={{ lineHeight: "1.2" }}>
                    {item.text}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        }
      </List>
    );
  };

  return (
    <Box>
      <>
        <GetInformation
          setUserName={setUserName}
          setUserEmail={setUserEmail}
          setCookie={setCookie}
          removeCookie={removeCookie}
          setVersionId={setVersionId}
        />
        {/*******************************************************************
           //  代理店／受注日／出荷予定日／登録者
          *******************************************************************/}
        <TableContainer component={Paper} sx={{ marginTop: "10px" }}>
          <Table>
            {renderOnlyItemTableHeader()}
            <TableBody>
              {renderOnlyItemTableData(
                new Date().toLocaleDateString(),
                new Date().toLocaleDateString(),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
      <Box sx={{ mt: 4, mb: 1 }}>
        <Typography>購入申請書＆注文書類</Typography>
      </Box>
      {/****************************************************
       //  注意書き、ファイル命名規則
       //****************************************************/}
      <Box sx={{ mt: 0, mb: 1 }}>
        <>
          <RenderList
            handleClick={handleClick}
            open={open}
            apiID={sale_agent.apiID}
          />
        </>
      </Box>
      {/****************************************************
       //  PDF ファイルのドロップエリア（購入申請書）
       //****************************************************/}
      {uploadedFiles.length === 0 ? (
        <Dropzone
          onDrop={(acceptedFiles) => {
            // 新しいファイルを追加する前に、すでにアップロードされているファイルをチェックします。
            const newFiles = acceptedFiles.filter((file) => {
              return !filesToUpload.some(
                (existingFile) => existingFile.name === file.name,
              );
            });
            // ファイルタイプがPDFか確認
            const isPdf = newFiles.every(
              (file) => file.type === "application/pdf",
            );
            if (!isPdf) {
              // エラーメッセージを表示
              alert("PDFファイルのみ選択が可能です。");
              return;
            }

            newFiles.forEach((file) => {
              setUploadFileKind((prevStatus) => ({
                ...prevStatus,
                [file.name]: checkOrderForm(sale_agent.apiID, file.name),
              }));
            });
            setFilesToUpload((prevFiles) => [...prevFiles, ...newFiles]);
          }}
        >
          {({ getRootProps, getInputProps }) => (
            <section>
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed #cccccc",
                  padding: "20px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#f5f5f5",
                  color: "#444444",
                }}
              >
                <input {...getInputProps()} accept="application/pdf" />
                <p>
                  {sale_agent.apiID === AGENCY_TOO_INDEX
                    ? "Tooの購入申請書および発注書、注文書のPDFファイルを"
                    : sale_agent.apiID === AGENCY_SNC_INDEX
                      ? "TD-SYNNEX の購入申請書と注文書が格納されているPDFファイルを"
                      : sale_agent.apiID === AGENCY_DIS_INDEX
                        ? "DIS の購入申請書と注文書が格納されているPDFファイルを"
                        : "SB C&S の購入申請書と発注書が格納されているPDFファイルを"}
                  <br />
                  ドラッグ＆ドロップするか、ここをクリックしてファイルを指定してください
                </p>
              </Box>
            </section>
          )}
        </Dropzone>
      ) : (
        <p sx={{ color: "gray" }}></p>
      )}
      <Box
        sx={{ mt: 3, mb: 3 }}
        style={{ cursor: isWaitCursor ? "wait" : "default" }}
      >
        {(uploadPhase === UPLOAD_PHASES.ERROR || hasUploadFailure) && (
          <Typography variant="body2" sx={{ mb: 1, color: "#d32f2f" }}>
            エラー発生時の状態: {getUploadErrorPhaseMessage()}
            {uploadSessionPath && ` / path: ${uploadSessionPath}`}
          </Typography>
        )}
        {/****************************************************
           //  選択された PDF ファイル
           *****************************************************/}
        {filesToUpload.length > 0 && (
          <>
            <Typography>
              選択された PDF ファイル（ {filesToUpload.length} ）
            </Typography>
            <Table sx={{ mt: "4px", mb: "4px" }}>
              <tbody>
                {filesToUpload.map((file, index) => (
                  <TableRow key={file.name}>
                    <TableCell style={{ padding: 0 }}>
                      {sale_agent.apiID === AGENCY_SNC_INDEX ? (
                        ""
                      ) : uploadfileKind[file.name].startsWith(
                          "purchaseOrder",
                        ) ? (
                        "購入申請書"
                      ) : sale_agent.apiID === AGENCY_TOO_INDEX ? (
                        uploadfileKind[file.name] === "orderForm" ? (
                          "注文書"
                        ) : uploadfileKind[file.name] === "orderForm_th" ? (
                          "発注書"
                        ) : (
                          <span style={{ color: "#ff7f7f" }}>無効</span>
                        )
                      ) : (
                        "注文書類"
                      )}
                    </TableCell>
                    <TableCell style={{ padding: 0 }}>
                      {uploadedFiles.length === 0 ? (
                        <a
                          href={file.preview || URL.createObjectURL(file)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {file.name}（
                          {file.size / 1024 > 1024
                            ? (file.size / 1024 / 1024)
                                .toFixed(2)
                                .toLocaleString() + " MB"
                            : Math.floor(file.size / 1024).toLocaleString() +
                              " KB"}
                          ）
                        </a>
                      ) : (
                        <span>{file.name}</span>
                      )}{" "}
                      {uploadStatus[file.name] === "Uploading" && (
                        <span style={{ color: "blue" }}>- Uploading...</span>
                      )}
                      {uploadStatus[file.name] === "Upload failed" && (
                        <span style={{ color: "red" }}>- Upload failed</span>
                      )}
                      {uploadStatus[file.name] === "Uploaded" && (
                        <span style={{ color: "gray" }}>- Uploaded</span>
                      )}
                      {""}
                      <Button
                        onClick={() => handleDeleteFile(index)}
                        sx={{ marginRight: "0px" }}
                        disabled={
                          isWaitCursor ||
                          uploadStatus[file.name] === "Uploading" ||
                          uploadStatus[file.name] === "Uploaded"
                        }
                      >
                        &lt;取消&gt;
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            <p>
              <Button
                variant="contained"
                color="primary"
                disabled={isRegisterButtonDisabled}
                onClick={handleUpload}
              >
                {isRetryMode ? "再試行" : "登録"}
              </Button>
            </p>
          </>
        )}
        {/*****************************************************
          //  アップロード済み PDF ファイル
          ******************************************************/}
        {uploadedFiles.length > 0 && (
          <>
            <Typography>
              処理中はブラウザを終了したり、ページ遷移は行わず「完了」をお待ちください。
              <br />
              アップロード済み PDF ファイル（ {uploadedFiles.length} ）{" "}
            </Typography>
            <ul>
              {uploadedFiles.map((uploadedFile, index) => (
                <li key={index}>
                  {uploadedFile.url ? (
                    <a
                      href={uploadedFile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {uploadedFile.fileName}
                    </a>
                  ) : (
                    <span>{uploadedFile.fileName}</span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {/*****************************************************
        //  終了
        *****************************************************/}
        {apiCompleteStatus === "ok" ? (
          <p>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAllClear}
            >
              完了
            </Button>
          </p>
        ) : (
          (apiCompleteStatus.length >= 2 || hasUploadFailure) && (
            <p>
              {apiCompleteStatus.length >= 2
                ? apiCompleteStatus
                : "Upload failed"}
              <br />
              <br />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleAllClear}
              >
                エラー終了
              </Button>
            </p>
          )
        )}
        {/*****************************************************
        //  終了
        *****************************************************/}
        {errorMessage && (
          <>
            <div className="error-message">{errorMessage}</div>
            <p>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAllClear}
              >
                完了
              </Button>
            </p>
          </>
        )}
      </Box>
    </Box>
  );
}

export default FileUploader;
